// Design lab (dev-only): /variants?v=<name> renders ONE surface per load so
// screenshots are clean. Round-A variants are decided and deleted — what stays
// is the lab itself plus reference states for whatever round is live.
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Hero } from '../sections/hero-v2';
import { SiteNav, type ShowcaseItem } from '../sections/hero';
import { FeatureShowcase } from '../sections/feature-showcase-v2';
import { FinalCTA } from '../sections/final-lantern';
import { App } from '../App';
import '../styles/variants.css';

// same items as App.tsx's visual showcase
const visual: ShowcaseItem[] = [
  { icon: 'search', title: 'Natural language search', text: 'Type what you remember — “astronauts working on the lunar surface” — and the perfect shot surfaces instantly.', scene: 'visual-search' },
  { icon: 'layers', title: 'Context picker', text: 'Choose exactly which media to search: a folder, a drive, a bin — or everything you’ve ever indexed.', scene: 'context-picker' },
  { icon: 'image', title: 'Search by image', text: 'Drop in a reference frame or a still. ClipRadar finds every shot that looks like it.', scene: 'image-search' },
  { icon: 'sparkle', title: 'Match similar', text: 'Found one good take? One click surfaces every visually similar moment across your library.', scene: 'match-similar' },
];

const SHOW_SUB = 'ClipRadar has watched every frame you’ve indexed. Search your footage the way you remember it — by what’s on screen.';

const VARIANTS: Record<string, { note: string; el: React.ReactNode }> = {
  'hero-current': { note: 'HERO · branch state (A1 landed)', el: <Hero /> },
  'nav-current': { note: 'NAV · branch state (N1 pill)', el: <><SiteNav /><Hero /></> },
  'show-current': {
    note: 'SHOWCASE · branch state (S1 baked)',
    el: <div style={{ padding: '60px 0 40px' }}><FeatureShowcase id="visual" label="Visual search" title="Describe the shot. Get the shot." sub={SHOW_SUB} items={visual} /></div>,
  },
  // closing rework DECIDED (2026-07-17): the ping (radar sunrise) won and lives
  // in final-lantern.tsx — the losing variants (library/beacon/monolith) died
  // with closing-variants.tsx; this entry shows the live closing.
  'closing-current': { note: 'CLOSING · the ping (live)', el: <div style={{ paddingTop: 90 }}><FinalCTA /></div> },
  // Round-C DECIDED: B1.5 glacier baked into tokens.css :root — this entry is
  // just the whole page in the lab (handy for full-page shots without the router)
  'world-current': { note: 'LIGHT · glacier (baked)', el: <App /> },
};

export function VariantsPage() {
  const v = new URLSearchParams(window.location.search).get('v') ?? '';
  const hit = VARIANTS[v];

  // App.tsx normally runs the scroll-reveal observer; here every section is lit
  // immediately so ignite/reveal states fire without scrolling.
  useEffect(() => {
    document.title = 'ClipRadar — design lab';
    const t = window.setTimeout(() => {
      document.querySelectorAll('section').forEach((s) => s.classList.add('crw-in'));
    }, 120);
    return () => window.clearTimeout(t);
  }, [v]);

  if (!hit) {
    return (
      <div style={{ minHeight: '100vh', padding: '90px 24px', maxWidth: 640, margin: '0 auto' }}>
        <h1 style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 28 }}>Design lab</h1>
        <p style={{ color: 'var(--fg-2)', fontSize: 14.5 }}>One surface per load (clean screenshots):</p>
        <ul style={{ display: 'flex', flexDirection: 'column', gap: 8, listStyle: 'none', padding: 0 }}>
          {Object.entries(VARIANTS).map(([k, { note }]) => (
            <li key={k}><Link to={`/variants?v=${k}`} style={{ color: 'var(--signal-300)', fontFamily: 'var(--font-mono)', fontSize: 13 }}>{k}</Link><span style={{ color: 'var(--fg-3)', fontSize: 12.5, marginLeft: 10 }}>{note}</span></li>
          ))}
        </ul>
      </div>
    );
  }
  return (
    <div>
      <span className="vlab-label">{hit.note}</span>
      {hit.el}
    </div>
  );
}
